import { useEffect, useState } from 'react'
import cx from 'classnames'
import { observer } from 'mobx-react-lite'
import Styles from './BottomButtons.module.scss'
import { focusModeSet } from 'redux/actions/focusMode'
import store from 'redux/store'
import { useCurrentArticleId } from 'hooks/useCurrentArticleId'
import { Help } from 'blaze-ui/icons/16'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { HelpDropdown } from 'blaze-components/TopBar/HelpDropdown/HelpDropdown'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { WizardType } from 'components/Wizards/schemas/Types'
import { useRootStore } from 'stores/RootStore'

export const BottomButtons = ({
  videoTutorialType,
  isSaveFooterActive = false,
  withGenerationBar = false,
  withQuestionnairePanel = false,
}: {
  videoTutorialType?: WizardType
  isSaveFooterActive?: boolean
  withGenerationBar?: boolean
  withQuestionnairePanel?: boolean
}) => {
  const currentArticleId = useCurrentArticleId()

  useEffect(() => {
    const onEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        store.dispatch(focusModeSet({ showFocusMode: false, manual: false }))
      }
    }
    document.body.addEventListener('keydown', onEscape)
    return () => document.body.removeEventListener('keydown', onEscape)
  }, [])

  const trigger = (
    <IconButton rounded aria-label="Help" size="sm" useChildClass variant="secondary" ignoreHover icon={Help} />
  )

  return (
    <div
      className={cx(Styles.bottomContainer, {
        [Styles.withSaveFooter]: isSaveFooterActive,
        [Styles.withGenerationBar]: withGenerationBar,
        [Styles.withQuestionnairePanel]: withQuestionnairePanel,
      })}
    >
      <div className={Styles.bottomButtons}>
        {!currentArticleId && (
          <HelpDropdown
            trigger={trigger}
            allowOnboarding={false}
            placement="top-start"
            videoTutorialType={videoTutorialType}
          />
        )}
      </div>
    </div>
  )
}

const useIsFullscreenModalOpen = () => {
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    const checkFullscreenModal = () => {
      setIsOpen(!!document.querySelector('[class*="wrapper"][class*="fullscreen"]'))
    }

    const observer = new MutationObserver(checkFullscreenModal)
    observer.observe(document.body, { childList: true, subtree: true })
    checkFullscreenModal()

    return () => observer.disconnect()
  }, [])

  return isOpen
}

const useIsSaveFooterActive = () => {
  const [isActive, setIsActive] = useState(false)

  useEffect(() => {
    const checkSaveFooter = () => {
      setIsActive(!!document.querySelector('[data-save-footer-active]'))
    }

    const observer = new MutationObserver(checkSaveFooter)
    observer.observe(document.body, { childList: true, subtree: true })
    checkSaveFooter()

    return () => observer.disconnect()
  }, [])

  return isActive
}

export const BottomButtonsContainer = observer(() => {
  const { isMobile } = useResponsiveDesign()
  const isFullscreenModalOpen = useIsFullscreenModalOpen()
  const isSaveFooterActive = useIsSaveFooterActive()
  const { contentGeneration } = useRootStore()

  if (isMobile) return null
  if (isFullscreenModalOpen) return null

  const questionnaireExpanded =
    contentGeneration.isActive && contentGeneration.showQuestionnaire && !contentGeneration.isQuestionnaireMinimized

  return (
    <BottomButtons
      isSaveFooterActive={isSaveFooterActive}
      withGenerationBar={contentGeneration.isVisible && !questionnaireExpanded}
      withQuestionnairePanel={questionnaireExpanded}
    />
  )
})
